import axios from "axios";
import { useEffect, useRef } from "react";
import { getReceipt, receiptByConcept } from "./../graphql/queries";
import {
  ModelReceiptFilterInput,
  UpdateReceiptInput,
} from "./../models/GQL_API";
import { deleteReceipt, updateReceipt } from "./../graphql/mutations";
import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { useDispatch, useSelector } from "react-redux";
import {
  setListing,
  setListingReceiptsStats,
  setSearchText,
  setSelected,
  setNextToken,
  changeLimit,
  setPreviousToken,
  nextAction,
} from "../store/ducks/receipt";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import useTimeline from "./useTimeline";
import { listReceiptsCustom } from "../graphqlCustom/queries";
import { Receipt } from "../API";
import { TransactionListingVariables, TransactionStats } from "../models/app";
import { INVOICE_STATUS, TimelineActions } from "../constants/enums";
import { EG_OPERATIONS, EG_SEND_INVOICE } from "../constants/endPoints";

// Local optimized query for receipts listing that EXCLUDES updatedAt to avoid DateTime serialization errors
const receiptByConceptLite = /* GraphQL */ `
  query ReceiptByConcept(
    $conceptID: ID!
    $checkNumber: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelReceiptFilterInput
    $limit: Int
    $nextToken: String
  ) {
    receiptByConcept(
      conceptID: $conceptID
      checkNumber: $checkNumber
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        conceptID
        checkNumber
        uuid
        submissionId
        header {
          dateTimeIssued
          receiptNumber
          uuid
          previousUUID
          currency
          exchangeRate
          grossWeight
          netWeight
          __typename
        }
        documentType {
          receiptType
          typeVersion
          __typename
        }
        seller {
          rin
          companyTradeName
          branchCode
          __typename
        }
        buyer {
          type
          id
          name
          mobileNumber
          paymentNumber
          __typename
        }
        itemData {
          internalCode
          description
          itemType
          itemCode
          unitType
          quantity
          unitPrice
          netSale
          totalSale
          total
          commercialDiscountData {
            amount
            description
            __typename
          }
          itemDiscountData {
            amount
            description
            __typename
          }
          valueDifference
          taxableItems {
            taxType
            amount
            subType
            rate
            __typename
          }
          __typename
        }
        totalSales
        totalCommercialDiscount
        totalItemsDiscount
        netAmount
        feesAmount
        totalAmount
        taxTotals {
          taxType
          amount
          __typename
        }
        paymentMethod
        qrString
        qrImage
        status
        reason
        type
        returned
        deleted
        createdAt
        createdByID
        createdByName
        __typename
      }
      nextToken
      __typename
    }
  }
`;

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();
  const { timelinesCreate } = useTimeline("timelines", "timeline");

  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );

  const storedLimit = useSelector(
    (state: any) => state[`${listingName}`]["limit"]
  );

  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]["nextToken"]
  );
  const previousTokens = useSelector(
    (state: any) => state[`${listingName}`]["previousTokens"]
  );

  // In-memory page history: stores arrays of items for each visited page in order
  // Reset when pagination resets (limit change/startIndex === 0)
  const pageHistoryRef = useRef<any[]>([]);

  // Persist per-page limit to localStorage and hydrate on load
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const savedLimit = window.localStorage.getItem(`perPage:${listingName}`);
      if (savedLimit) {
        const savedNum = parseInt(savedLimit, 10);
        if (!Number.isNaN(savedNum) && savedNum !== storedLimit) {
          dispatch(changeLimit(savedNum));
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(
        `perPage:${listingName}`,
        String(storedLimit ?? "")
      );
    } catch {}
  }, [listingName, storedLimit]);

  // async function fetch(props: TransactionListingVariables) {
  //   try {
  //     const {
  //       startIndex,
  //       limit,
  //       moveForward,
  //       conceptID,
  //       searchText,
  //       fromDate,
  //       toDate,
  //       conceptsSelectedFilters,
  //     } = props;

  //     let requestLimit = storedLimit;
  //     let requestToken = nextToken;
  //     let requestPreviousTokens = previousTokens;

  //     // Clear pagination data
  //     if (limit !== storedLimit || startIndex === 0) {
  //       requestLimit = limit;
  //       requestToken = null;
  //       requestPreviousTokens = [];

  //       dispatch(changeLimit(limit));
  //     }

  //     // Get token from previous tokens
  //     if (!moveForward) {
  //       const updatedPreviousTokens = [...requestPreviousTokens];
  //       updatedPreviousTokens.pop();

  //       requestToken =
  //         updatedPreviousTokens.length >= 2
  //           ? updatedPreviousTokens[updatedPreviousTokens.length - 2]
  //           : null;
  //       dispatch(
  //         setNextToken(updatedPreviousTokens[updatedPreviousTokens.length - 1])
  //       );
  //       dispatch(setPreviousToken(updatedPreviousTokens));
  //     }

  //     const filter: ModelReceiptFilterInput = {
  //       deleted: { eq: "0" },
  //     };

  //     // Search Filter (exact checkNumber match when provided)
  //     if (searchText && String(searchText).trim().length > 0) {
  //       filter.checkNumber = { eq: String(searchText).trim() };
  //     }

  //     // Date Filter
  //     // if (fromDate && toDate) {
  //     //   filter.and = [];

  //     //   filter.and.push({
  //     //     createdAt: { ge: "1761331200000" },
  //     //   });
  //     //   filter.and.push({
  //     //     createdAt: {
  //     //       lt: "1761417599000",
  //     //     },
  //     //   });
  //     // }

  //     // Single-page fetch respecting per-page limit and tokens
  //     let page: any = null;
  //     try {
  //       const receiptList: any = await API.graphql<Receipt>({
  //         query: receiptByConcept,
  //         variables: {
  //           conceptID: conceptID,
  //           filter,
  //           limit: requestLimit,
  //           nextToken: requestToken,
  //         },
  //         authMode: true
  //           ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
  //           : GRAPHQL_AUTH_MODE.AWS_IAM,
  //       });
  //       page = receiptList?.data?.receiptByConcept;
  //     } catch (e: any) {
  //       if (e?.data?.receiptByConcept) {
  //         page = e.data.receiptByConcept;
  //       } else {
  //         throw e;
  //       }
  //     }

  //     const items = page?.items || [];

  //     if (moveForward) {
  //       const currentNextToken = page?.nextToken || null;
  //       dispatch(nextAction(currentNextToken, requestPreviousTokens));
  //     }

  //     return items;
  //   } catch (err: Error | any) {
  //     showError(
  //       typeof err.message === "string" ? err.message : "Error occurred"
  //     );
  //     console.log(err);
  //   }
  // }
  async function fetch(props: TransactionListingVariables) {
    try {
      const {
        startIndex,
        limit,
        moveForward,
        conceptID,
        searchText,
        fromDate,
        toDate,
        conceptsSelectedFilters,
        status,
      } = props;

      let requestLimit = storedLimit;
      let requestToken = nextToken;
      let requestPreviousTokens = previousTokens;

      // Clear pagination data
      if (limit !== storedLimit || startIndex === 0) {
        requestLimit = limit;
        requestToken = null;
        requestPreviousTokens = [];

        dispatch(changeLimit(limit));
        pageHistoryRef.current = [];
      }

      // Get token from previous tokens (when moving backward)
      if (!moveForward) {
        const updatedPreviousTokens = [...requestPreviousTokens];
        updatedPreviousTokens.pop();

        requestToken =
          updatedPreviousTokens.length >= 2
            ? updatedPreviousTokens[updatedPreviousTokens.length - 2]
            : null;
        dispatch(
          setNextToken(updatedPreviousTokens[updatedPreviousTokens.length - 1])
        );
        dispatch(setPreviousToken(updatedPreviousTokens));
      }

      // checkNumber as key condition (not in filter)
      const checkNumberKeyCondition =
        searchText && String(searchText).trim().length > 0
          ? { eq: String(searchText).trim() }
          : undefined;

      // Determine the conceptID to use for receiptByConcept query
      const effectiveConceptID =
        conceptID ||
        (conceptsSelectedFilters && conceptsSelectedFilters.length > 0
          ? conceptsSelectedFilters[0].id
          : null);

      // Validate conceptID is available when using receiptByConcept query
      // Don't fetch if no concept is available (new account without concepts)
      if (!effectiveConceptID) {
        dispatch(setListing([]));
        return [];
      }

      // When searching by checkNumber, we only need to fetch once (exact match)
      // Otherwise, use multi-page fetch loop
      const isSearchingByCheckNumber = !!checkNumberKeyCondition;

      // When searching by checkNumber, only filter by deleted (required) and conceptID
      // Skip date and status filters to allow searching across all dates and statuses
      const filter: ModelReceiptFilterInput = {
        deleted: { eq: "0" },
      };

      // Only apply time and status filters when NOT searching by checkNumber
      if (!isSearchingByCheckNumber) {
        // Time filter
        if (fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);

          filter.and = filter.and || [];
          filter.and.push({ createdAt: { ge: String(from.getTime()) } });
          filter.and.push({ createdAt: { lt: String(to.getTime()) } });
        }

        // Status filter (VALID | INVALID). Default is ALL (no filter)
        if (status && status !== "ALL") {
          const statusValue = status === "VALID" ? "valid" : "invalid";
          filter.and = filter.and || [];
          filter.and.push({ status: { eq: statusValue } });
        } else {
        }
      } else {
      }

      let collected: any[] = [];
      let localNextToken: string | null = requestToken ?? null;
      let lastPageNextToken: string | null = null;

      // For checkNumber search, limit to single page fetch
      const maxPages = isSearchingByCheckNumber ? 1 : undefined;
      let pageCount = 0;

      do {
        pageCount++;
        const pageLimit = Math.min(1000, requestLimit - collected.length);
        try {
          const variables: any = {
            conceptID: effectiveConceptID,
            filter,
            limit: isSearchingByCheckNumber ? requestLimit : 100000,
            nextToken: localNextToken,
            sortDirection: "DESC",
          };
          if (checkNumberKeyCondition) {
            variables.checkNumber = checkNumberKeyCondition;
          }


          const receiptList: any = await API.graphql<Receipt>({
            query: receiptByConceptLite,
            variables,
            authMode: true
              ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
              : GRAPHQL_AUTH_MODE.AWS_IAM,
          });

          const page = receiptList?.data?.receiptByConcept;
          if (!page) {
            break;
          }

          const pageItems = (page?.items || []).filter((it: any) => it);
          lastPageNextToken = page?.nextToken ?? null;
          collected = collected.concat(pageItems);

          // For checkNumber search, stop after first page (exact match, no need to paginate)
          if (isSearchingByCheckNumber) {
            break;
          }

          localNextToken = lastPageNextToken;
        } catch (e: any) {
          if (e?.data?.receiptByConcept) {
            const page = e.data.receiptByConcept;
            const pageItems = (page?.items || []).filter((it: any) => it);
            lastPageNextToken = page?.nextToken ?? null;
            collected = collected.concat(pageItems);
            localNextToken = lastPageNextToken;
            // For checkNumber search, stop after handling error response
            if (isSearchingByCheckNumber) break;
          } else {
            // If query fails, break the loop and show error
            if (checkNumberKeyCondition) {
              showError(
                `Error searching for check number: ${
                  e?.message || "Unknown error"
                }`
              );
            } else {
              showError(
                `Error fetching receipts: ${e?.message || "Unknown error"}`
              );
            }
            // Break the loop instead of throwing to ensure listing is updated
            break;
          }
        }

        if (collected.length >= requestLimit) break;
        if (maxPages && pageCount >= maxPages) break;
      } while (localNextToken);

      // Enforce per-page display: trim to the requested page size
      if (Array.isArray(collected) && collected.length > requestLimit) {
        collected = collected.slice(0, requestLimit);
      }

      // Ensure collected is always an array
      if (!Array.isArray(collected)) {
        collected = [];
      }


      // Update Redux state for pagination (store the next token after the last fetched page)
      if (moveForward) {
        const currentNextToken = lastPageNextToken || null;
        dispatch(nextAction(currentNextToken, requestPreviousTokens));
      }

      // Update Redux with the fetched data (always update, even if empty)
      dispatch(setListing(collected));

      return Array.isArray(collected) ? collected : [];
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
      // Ensure listing is updated even on error to prevent loading state
      dispatch(setListing([]));
      return [];
    }
  }

  async function normalFetch(props: TransactionListingVariables) {
    try {
      const {
        startIndex,
        limit,
        moveForward,
        conceptID,
        searchText,
        fromDate,
        toDate,
        conceptsSelectedFilters,
      } = props;

      let requestLimit = storedLimit;
      let requestToken = nextToken;
      let requestPreviousTokens = previousTokens;

      // Clear pagination data
      if (limit !== storedLimit || startIndex === 0) {
        requestLimit = limit;
        requestToken = null;
        requestPreviousTokens = [];

        dispatch(changeLimit(limit));
      }

      // Get token from previous tokens
      if (!moveForward) {
        const updatedPreviousTokens = [...requestPreviousTokens];
        updatedPreviousTokens.pop();

        requestToken =
          updatedPreviousTokens.length >= 2
            ? updatedPreviousTokens[updatedPreviousTokens.length - 2]
            : null;
        dispatch(
          setNextToken(updatedPreviousTokens[updatedPreviousTokens.length - 1])
        );
        dispatch(setPreviousToken(updatedPreviousTokens));
      }

      // If searching by checkNumber and we have a conceptID, use receiptByConcept query with index
      if (
        searchText &&
        conceptID &&
        (!conceptsSelectedFilters || conceptsSelectedFilters.length === 0)
      ) {
        const filter: ModelReceiptFilterInput = {
          deleted: { eq: "0" },
        };

        // Date Filter
        if (fromDate && toDate) {
          filter.and = filter.and || [];
          filter.and.push({
            createdAt: { ge: new Date(fromDate).getTime().toString() },
          });
          filter.and.push({
            createdAt: {
              lt: new Date(toDate).getTime().toString(),
            },
          });
        }

        // checkNumber as key condition (uses index for faster search)
        const checkNumberKeyCondition =
          searchText && String(searchText).trim().length > 0
            ? { eq: String(searchText).trim() }
            : undefined;

        const variables: any = {
          conceptID,
          filter,
          limit: requestLimit,
          nextToken: requestToken,
          sortDirection: "DESC",
        };

        if (checkNumberKeyCondition) {
          variables.checkNumber = checkNumberKeyCondition;
        }

        const receiptList: any = await API.graphql<Receipt>({
          query: receiptByConceptLite,
          variables,
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        if (moveForward) {
          const currentNextToken = receiptList.data.receiptByConcept.nextToken;
          dispatch(nextAction(currentNextToken, requestPreviousTokens));
        }

        const listing = (receiptList.data.receiptByConcept.items || []).filter(
          (it: any) => it
        );

        return listing;
      }

      // Fallback to listReceipts for multi-concept scenarios or when conceptID is not available
      const filter: ModelReceiptFilterInput = {
        deleted: { eq: "0" },
      };

      // Search Filter (only used as fallback when receiptByConcept can't be used)
      if (searchText) {
        filter.checkNumber = { contains: searchText };
      }

      // Concepts Filter
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        filter.or = [];

        for (let conceptsSelected of conceptsSelectedFilters) {
          filter.or.push({ conceptID: { eq: conceptsSelected.id } });
        }
      } else {
        if (conceptID) filter.conceptID = { eq: conceptID };
      }

      // Date Filter
      if (fromDate && toDate) {
        filter.and = filter.and || [];

        filter.and.push({
          createdAt: { ge: new Date(fromDate).getTime().toString() },
        });
        filter.and.push({
          createdAt: {
            lt: new Date(toDate).getTime().toString(),
          },
        });
      }

      const receiptList: any = await API.graphql<Receipt>({
        query: listReceiptsCustom,
        variables: { filter, limit: requestLimit, nextToken: requestToken },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      if (moveForward) {
        const currentNextToken = receiptList.data.listReceipts.nextToken;
        dispatch(nextAction(currentNextToken, requestPreviousTokens));
      }

      const listing = (receiptList.data.listReceipts.items || []).filter(
        (it: any) => it
      );

      return listing;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  /**
   * Optimized fetch function for time-based filtering with createdAt sorting
   * Uses receiptByConcept query with proper indexing for better performance
   * Sorts by createdAt in descending order (newest first)
   */
  async function fetchByTimeRange(props: TransactionListingVariables) {
    try {
      const {
        startIndex,
        limit,
        moveForward,
        conceptID,
        searchText,
        fromDate,
        toDate,
        conceptsSelectedFilters,
      } = props;

      let requestLimit = storedLimit;
      let requestToken = nextToken;
      let requestPreviousTokens = previousTokens;

      // Clear pagination data
      if (limit !== storedLimit || startIndex === 0) {
        requestLimit = limit;
        requestToken = null;
        requestPreviousTokens = [];

        dispatch(changeLimit(limit));
      }

      // Get token from previous tokens (when moving backward)
      if (!moveForward) {
        const updatedPreviousTokens = [...requestPreviousTokens];
        updatedPreviousTokens.pop();

        requestToken =
          updatedPreviousTokens.length >= 2
            ? updatedPreviousTokens[updatedPreviousTokens.length - 2]
            : null;
        dispatch(
          setNextToken(updatedPreviousTokens[updatedPreviousTokens.length - 1])
        );
        dispatch(setPreviousToken(updatedPreviousTokens));
      }

      const filter: ModelReceiptFilterInput = {
        deleted: { eq: "0" },
      };

      // TIME FILTER - Optimized with proper date range filtering
      if (fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);

        filter.and = filter.and || [];
        filter.and.push({
          createdAt: { ge: String(from.getTime()) },
        });
        filter.and.push({
          createdAt: { lt: String(to.getTime()) },
        });
      }

      // Prepare checkNumber key condition for exact search
      const checkNumberKeyCondition =
        searchText && String(searchText).trim().length > 0
          ? { eq: String(searchText).trim() }
          : undefined;

      // Single-page fetch with optimized query (lite selection set)
      let page: any = null;
      try {
        const variables: any = {
          conceptID,
          filter,
          limit: requestLimit,
          nextToken: requestToken,
          sortDirection: "DESC", // Sort by createdAt descending (newest first)
        };

        // Add checkNumber key condition if searching
        if (checkNumberKeyCondition) {
          variables.checkNumber = checkNumberKeyCondition;
        }

        const receiptList: any = await API.graphql<Receipt>({
          query: receiptByConceptLite,
          variables,
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        page = receiptList?.data?.receiptByConcept;
      } catch (e: any) {
        if (e?.data?.receiptByConcept) {
          page = e.data.receiptByConcept;
        } else {
          throw e;
        }
      }

      const items = (page?.items || []).filter((it: any) => it);

      // Update pagination state if moving forward
      if (moveForward) {
        const currentNextToken = page?.nextToken || null;
        dispatch(nextAction(currentNextToken, requestPreviousTokens));
      }

      // Dispatch current page items to Redux store
      dispatch(setListing(items));

      // Always return an array (remaining items if it's the last page)
      return Array.isArray(items) ? items : [];
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  /*
   * Fetch all receipt within selected range
   * Filter then using receipt status (Valid, Invalid, Rejected, Cancelled)
   * Return Hash map contains (Status, # of receipts, total, percentage)
   */
  async function fetchStats(props: TransactionListingVariables) {
    try {
      const { limit, conceptID, fromDate, toDate, conceptsSelectedFilters } =
        props;

      // If a specific concept is selected (and no multi-concept filter),
      // use the indexed query with full pagination to fetch all receipts for the day.
      if (
        conceptID &&
        (!conceptsSelectedFilters || conceptsSelectedFilters.length === 0)
      ) {
        const filter: ModelReceiptFilterInput = { deleted: { eq: "0" } };
        if (fromDate && toDate) {
          filter.and = filter.and || [];
          filter.and.push({
            createdAt: { ge: String(new Date(fromDate).getTime()) },
          });
          filter.and.push({
            createdAt: { lt: String(new Date(toDate).getTime()) },
          });
        }

        let allItems: any[] = [];
        let localNextToken: string | null = null;
        do {
          const variables: any = {
            conceptID,
            filter,
            limit: 10000000,
            nextToken: localNextToken,
            sortDirection: "DESC",
          };

          const response: any = await API.graphql<Receipt>({
            query: receiptByConceptLite,
            variables,
            authMode: true
              ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
              : GRAPHQL_AUTH_MODE.AWS_IAM,
          });

          const page = response?.data?.receiptByConcept;
          const pageItems = (page?.items || []).filter((it: any) => it);
          allItems = allItems.concat(pageItems);
          localNextToken = page?.nextToken ?? null;
        } while (localNextToken);

        const stats = await receiptsStats(allItems as any);
        return stats;
      }

      // Fallback (multi-concept filters): use listReceipts (no pagination)
      const filter: ModelReceiptFilterInput = {
        deleted: { eq: "0" },
      };
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        filter.or = [];
        for (let conceptsSelected of conceptsSelectedFilters) {
          filter.or.push({ conceptID: { eq: conceptsSelected.id } });
        }
      } else if (conceptID) {
        filter.conceptID = { eq: conceptID };
      }
      if (fromDate && toDate) {
        filter.and = [];
        filter.and.push({
          createdAt: { ge: new Date(fromDate).getTime().toString() },
        });
        filter.and.push({
          createdAt: { lt: new Date(toDate).getTime().toString() },
        });
      }

      const receiptList: any = await API.graphql<Receipt>({
        query: listReceiptsCustom,
        variables: { filter, limit },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      const listing = receiptList?.data?.listReceipts?.items || [];
      const stats = await receiptsStats(listing);
      return stats;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function receiptsStats(listing: Receipt[]) {
    try {
      let validCounter: TransactionStats = {
        id: 1,
        status: INVOICE_STATUS.VALID,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#84c529",
      };

      let rejectedCounter: TransactionStats = {
        id: 2,
        status: INVOICE_STATUS.REJECTED,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#03a9f4",
      };

      let cancelledCounter: TransactionStats = {
        id: 3,
        status: INVOICE_STATUS.CANCELLED,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#ff6f00",
      };

      let invalidCounter: TransactionStats = {
        id: 4,
        status: INVOICE_STATUS.INVALID,
        counter: 0,
        total: 0,
        percentage: 0,
        fill: "#fc2d42",
      };

      let totalCounter: TransactionStats = {
        id: 5,
        status: "total",
        counter: 0,
        total: 0,
        percentage: 100,
        fill: "",
      };

      let totalReceipts = 0;
      let totalAmount = 0;

      listing.forEach(({ status, totalAmount }) => {
        switch (status) {
          case INVOICE_STATUS.VALID:
            validCounter.counter++;
            validCounter.total += totalAmount;
            break;
          case INVOICE_STATUS.REJECTED:
            rejectedCounter.counter++;
            rejectedCounter.total += totalAmount;
            break;
          case INVOICE_STATUS.CANCELLED:
            cancelledCounter.counter++;
            cancelledCounter.total += totalAmount;
            break;
          case INVOICE_STATUS.INVALID:
            invalidCounter.counter++;
            invalidCounter.total += totalAmount;
            break;
          default:
            break;
        }
      });

      totalReceipts =
        validCounter.counter +
        invalidCounter.counter +
        rejectedCounter.counter +
        cancelledCounter.counter;
      totalAmount =
        validCounter.total +
        invalidCounter.total +
        rejectedCounter.total +
        cancelledCounter.total;

      totalCounter.counter = totalReceipts;
      totalCounter.total = totalAmount;

      if (totalReceipts > 0) {
        validCounter.percentage = (validCounter.counter / totalReceipts) * 100;
        invalidCounter.percentage =
          (invalidCounter.counter / totalReceipts) * 100;
        rejectedCounter.percentage =
          (rejectedCounter.counter / totalReceipts) * 100;
        cancelledCounter.percentage =
          (cancelledCounter.counter / totalReceipts) * 100;
      }

      const receiptsStats: TransactionStats[] = [
        validCounter,
        rejectedCounter,
        cancelledCounter,
        invalidCounter,
        totalCounter,
      ];

      dispatch(setListingReceiptsStats(receiptsStats));
      return receiptsStats;
    } catch (err: Error | any) {
      throw err;
    }
  }

  async function get(id: string) {
    try {
      let single: Receipt | undefined;
      if (listing.length !== 0) {
        single = listing.find((resource: any) => resource.id === id);
      }

      if (single === undefined) {
        const listing: any = await API.graphql<Receipt>({
          query: getReceipt,
          variables: { id },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        single = listing.data.getReceipt;
      }

      return single;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  /*
   * Check if there is any change in receipt line
   * Recalculate totals
   * Create timeline
   * Update this receipt
   */
  async function update(id: string, data: any, editedLine: any) {
    try {
      const original = await get(id);

      if (original) {
        if (original.itemData.length !== data.itemData.length) {
          const timelineParams: any = {
            actionName: TimelineActions.Delete_TRX_LINE,
            oldStatus: editedLine.description,
            newStatus: "",
            transactionId: id,
          };

          await timelinesCreate(timelineParams);
        }

        const updateInput: UpdateReceiptInput = {
          id: original.id,
          itemData: data.itemData ? data.itemData : original!.itemData,
        };

        const updatedReceipt: any = await API.graphql<Receipt>({
          query: updateReceipt,
          variables: { input: updateInput },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        dispatch(setSelected(updatedReceipt));
      }

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function trash(id: string) {
    try {
      const original = await get(id);

      if (original) {
        const updateInput: UpdateReceiptInput = {
          id: original.id,
          deleted: "1",
        };

        await API.graphql<Receipt>({
          query: updateReceipt,
          variables: { input: updateInput },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
      }

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function bulkTrash(resourceIds: any) {
    for (let id of resourceIds) {
      try {
        await trash(id);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    dispatch(
      setListing(
        listing.filter((resource: any) => !resourceIds.has(resource.id))
      )
    );

    showConfirm(
      `${resourceIds.size} ${listingName} items has been moved to trash`
    );
  }

  async function remove(id: any) {
    try {
      await API.graphql<Receipt>({
        query: deleteReceipt,
        variables: { id: id },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id))
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  /*
   * Resend receipt to Egyptian Gateway/Cancel/Reject
   * Update receipt
   * Add timeline
   */
  async function resubmitReceipt(receipt: any, subOperation: string) {
    try {
      const operation = EG_OPERATIONS.RECEIPT;
      const url = EG_SEND_INVOICE;

      const response = await axios.post(
        `${url}?operation=${operation}&subOperation=${subOperation}`,
        {
          data: receipt,
        }
      );
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function exportAll(params: any) {
    try {
      let exportedData = [];
      const data: any = await fetch(params);

      for (let receipt of data!) {
        let row: any = { ...receipt };

        // Prepare custom fields
        if (row.seller) {
          row.seller = row.seller.companyTradeName;
        }
        if (row.buyer) {
          row.buyer = row.buyer.name;
        }

        exportedData.push(row);
      }

      return exportedData;
    } catch (err: any) {
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "conceptID",
      numeric: false,
      disablePadding: false,
      label: "Concept",
    },
    {
      id: "checkNumber",
      numeric: false,
      disablePadding: false,
      label: "Receipt Number",
    },
    {
      id: "seller",
      numeric: false,
      disablePadding: false,
      label: "Seller",
    },
    {
      id: "buyer",
      numeric: false,
      disablePadding: false,
      label: "Buyer",
    },
    {
      id: "totalAmount",
      numeric: false,
      disablePadding: false,
      label: "Total Amount",
    },
    {
      id: "currency",
      numeric: false,
      disablePadding: false,
      label: "Currency",
    },
    {
      id: "paymentMethod",
      numeric: false,
      disablePadding: false,
      label: "Payment Method",
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: "Status",
    },
    {
      id: "reason",
      numeric: false,
      disablePadding: false,
      label: "Reason",
    },
    {
      id: "uuid",
      numeric: false,
      disablePadding: false,
      label: "Document ID",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: false,
      disablePadding: false,
      label: "Actions",
    },
  ];

  const dataCells: readonly string[] = [
    "conceptID",
    "checkNumber",
    "seller",
    "buyer",
    "totalAmount",
    "currency",
    "paymentMethod",
    "status",
    "reason",
    "uuid",
    "submissionId",
  ];

  const lineHeadCells: readonly HeadCell[] = [
    {
      id: "internalCode",
      numeric: false,
      disablePadding: false,
      label: "Internal Code",
    },
    {
      id: "description",
      numeric: false,
      disablePadding: false,
      label: "Description",
    },
    {
      id: "itemCode",
      numeric: false,
      disablePadding: false,
      label: "Item Code",
    },
    {
      id: "itemType",
      numeric: false,
      disablePadding: false,
      label: "Item Type",
    },
    {
      id: "unitPrice",
      numeric: false,
      disablePadding: false,
      label: "Unit Price",
    },
    {
      id: "quantity",
      numeric: false,
      disablePadding: false,
      label: "Quantity",
    },
    {
      id: "netSale",
      numeric: false,
      disablePadding: false,
      label: "Net Sale",
    },
    {
      id: "totalSale",
      numeric: false,
      disablePadding: false,
      label: "Total Sale",
    },
    {
      id: "total",
      numeric: false,
      disablePadding: false,
      label: "Total",
    },
    {
      id: "unitType",
      numeric: false,
      disablePadding: false,
      label: "Unit Type",
    },
    {
      id: "valueDifference",
      numeric: false,
      disablePadding: false,
      label: "Value Difference",
    },
    {
      id: "actions",
      numeric: false,
      disablePadding: false,
      label: "Actions",
    },
  ];

  const options: any[] = [];

  if (Array.isArray(listing)) {
    for (let option of listing) {
      if (!option) continue;
      const label =
        (option as any).name ??
        (option as any).checkNumber ??
        (option as any).id ??
        "";
      const value = (option as any).id;
      if (value) options.push({ label, value });
    }
  }

  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}LineHeadCells`] = lineHeadCells;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByTimeRange`] = fetchByTimeRange;
  api[`${listingName}FetchStats`] = fetchStats;
  api[`${listingName}Get`] = get;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ResubmitReceipt`] = resubmitReceipt;
  api[`${listingName}Export`] = exportAll;

  api[`${listingName}ChangeListing`] = (listing: Receipt[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api[`${listingName}NextToken`] = nextToken;

  return api;
};

export default useResource;
